import React, { useState, useEffect, useRef, useCallback } from 'react';
import InterviewQuestionsSection from './InterviewQuestionsSection';
import { QuestionBankService, QuestionTemplate } from '../../../../services/QuestionBankService';
import { useCreateJobPosting, useUpdateJobPosting } from '../../../../hooks/mutations/useJobPostingMutations';
import type { CreateJobPostingRequest } from '@applicant-reviewer/api';
import { useHourlyRoles, useHourlyLocations } from '../../../../hooks/queries/useHourlyData';
import { useAuth } from '../../../../context/AuthContext';

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-[13px] text-gray-700 mb-1">{children}</label>
);

const Field: React.FC<{ span?: string; hint?: string; children: React.ReactNode; label?: string }> = ({ span = "col-span-12 sm:col-span-6 lg:col-span-4", hint, children, label }) => (
  <div className={span}>
    {label ? <Label>{label}</Label> : null}
    {children}
    {hint ? <div className="mt-1 text-[11px] text-gray-500">{hint}</div> : null}
  </div>
);

const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input {...props} className={"w-full h-9 px-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 " + (props.className || "")} />
);

const Textarea = (props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea {...props} className={"w-full min-h-[72px] px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 " + (props.className || "")} />
);

const Chip: React.FC<{ onRemove?: () => void; children: React.ReactNode }> = ({ onRemove, children }) => (
  <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 rounded-full px-2 h-7 text-xs">
    {children}
    {onRemove && (
      <button type="button" onClick={onRemove} className="w-4 h-4 grid place-items-center rounded-full hover:bg-red-100 text-red-600">×</button>
    )}
  </span>
);

const JOB_CATEGORIES = [
  "Accounting & Finance",
  "Administrative / Clerical",
  "Customer Service",
  "Sales & Business Development",
  "Marketing & Communications",
  "Human Resources",
  "Information Technology / Software",
  "Engineering",
  "Design / Creative",
  "Education & Training",
  "Healthcare & Medical",
  "Pharmaceutical / Biotechnology",
  "Science & Research",
  "Hospitality / Food Service",
  "Retail",
  "Manufacturing / Production",
  "Warehouse / Logistics",
  "Transportation / Delivery / Drivers",
  "Construction / Skilled Trades",
  "Real Estate / Property Management",
  "Legal",
  "Security & Protective Services",
  "Social Services / Nonprofit",
  "Government / Public Sector",
  "Energy / Utilities",
  "Agriculture / Farming",
  "Cleaning / Facilities / Maintenance",
  "Call Center / Support Center",
  "Quality Assurance / Compliance",
  "Other"
];

type QuestionType = "Text" | "Video" | "Multiple Choice";

interface Question {
  id: number;
  type: QuestionType;
  text: string;
  limit: string;
  choices: string[];
  newChoice: string;
}

interface JobFormSectionsProps {
  jobRole: string;
  selectedLocations: string[];
  onComplete?: () => void;
}

type SectionTab = 'details' | 'compensation' | 'questions' | 'preview';

// Generate unique 8-character ID
const generateJobId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export default function JobFormSections({ jobRole: _jobRole, selectedLocations, onComplete }: JobFormSectionsProps) {
  // jobRole is used by parent for identification, not displayed since it's shown in the tab
  const [activeSection, setActiveSection] = useState<SectionTab>('details');

  // Auto-save status
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');

  // Company data state (loaded from localStorage and updated via events)
  const [companyData, setCompanyData] = useState(() => {
    try {
      const saved = localStorage.getItem('passcom-company-data');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load company data:', error);
    }
    return {
      companyName: "TechCorp Solutions",
      industry: "",
      companyWebsite: "",
      companyAddress: "",
      contactEmail: "",
      companyDescription: "",
      companyLogo: null,
      welcomeVideo: null,
    };
  });

  // Hourly auth — token presence enables API calls
  const { hourlyToken } = useAuth();
  const hasHourlyToken = Boolean(hourlyToken);

  // Hourly data — job roles and locations from Hourly's API
  const hourlyRoles = useHourlyRoles(hasHourlyToken);
  const hourlyLocations = useHourlyLocations(hasHourlyToken);

  // Selected Hourly role/location IDs (user-facing dropdowns wired to Hourly data)
  const [selectedHourlyRoleId, setSelectedHourlyRoleId] = useState<string>('');
  const [selectedHourlyLocationId, setSelectedHourlyLocationId] = useState<string>('');

  // API mutation hooks
  const createJobPosting = useCreateJobPosting();
  const updateJobPosting = useUpdateJobPosting();
  // Track the backend-assigned job ID after first create (null = not yet saved to API)
  const [backendJobId, setBackendJobId] = useState<string | null>(null);

  // Form field state (start with defaults; no localStorage dependency)
  const [jobId, setJobId] = useState(generateJobId());
  const [title, setTitle] = useState("Line Cook");
  const [jobCategory, setJobCategory] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [skills, setSkills] = useState<string[]>([]);
  const [newSkill, setNewSkill] = useState("");
  const [jobType, setJobType] = useState("Full-time");
  const [locationType, setLocationType] = useState("On-site");
  const [address, setAddress] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  
  const [payType, setPayType] = useState("Hourly");
  const [payOption, setPayOption] = useState<"exact" | "range" | "omit">("exact");
  const [payExactAmount, setPayExactAmount] = useState("");
  const [payRangeMin, setPayRangeMin] = useState("");
  const [payRangeMax, setPayRangeMax] = useState("");
  const [benefits, setBenefits] = useState<string[]>([]);
  const [newBenefit, setNewBenefit] = useState("");
  const [tipEligible, setTipEligible] = useState(false);
  
  
  const [nonDiscriminationStatement, setNonDiscriminationStatement] = useState(
    "We are an Equal Opportunity Employer. All qualified applicants will receive consideration for employment without regard to race, color, religion, sex, sexual orientation, gender identity, national origin, age, disability, or veteran status."
  );
  
  const [questions, setQuestions] = useState<Question[]>(
    [{ id: 1, type: "Text", text: "", limit: "500", choices: [], newChoice: "" }]
  );

  const [interviewQuestions, setInterviewQuestions] = useState<Array<{id: string; text: string}>>(
    [{ id: 'iq-1', text: '' }, { id: 'iq-2', text: '' }, { id: 'iq-3', text: '' }]
  );
  
  // Question Bank templates
  const [applicationTemplates, setApplicationTemplates] = useState<QuestionTemplate[]>([]);
  const [interviewTemplates, setInterviewTemplates] = useState<QuestionTemplate[]>([]);
  
  // Use ref to always have access to latest form state without re-running effects
  const formStateRef = useRef({
    jobId, title, jobCategory, jobDescription, skills, jobType, locationType,
    address, address2, city, state, zip, payType, payOption, payExactAmount,
    payRangeMin, payRangeMax, benefits, tipEligible, nonDiscriminationStatement,
    questions, interviewQuestions
  });
  
  // Update ref whenever state changes (no re-render)
  useEffect(() => {
    formStateRef.current = {
      jobId, title, jobCategory, jobDescription, skills, jobType, locationType,
      address, address2, city, state, zip, payType, payOption, payExactAmount,
      payRangeMin, payRangeMax, benefits, tipEligible, nonDiscriminationStatement,
      questions, interviewQuestions
    };
  });
  
  // Pre-fill location from selected locations (use first location)
  useEffect(() => {
    if (selectedLocations && selectedLocations.length > 0) {
      const firstLocation = selectedLocations[0];
      // Map airport codes to sample addresses - replace with actual data as needed
      const locationMap: Record<string, { address: string; city: string; state: string; zip: string }> = {
        'BOS': { address: '1 Harborside Dr', city: 'Boston', state: 'MA', zip: '02128' },
        'LGA': { address: '104-04 Ditmars Blvd', city: 'Queens', state: 'NY', zip: '11369' },
        'DCA': { address: '2401 Smith Blvd', city: 'Arlington', state: 'VA', zip: '22202' },
        'ORD': { address: '10000 W Balmoral Ave', city: 'Chicago', state: 'IL', zip: '60666' }
      };
      
      const locationData = locationMap[firstLocation];
      if (locationData) {
        setAddress(locationData.address);
        setCity(locationData.city);
        setState(locationData.state);
        setZip(locationData.zip);
      }
    }
  }, [selectedLocations]);

  // Pre-fill address from selected Hourly location
  useEffect(() => {
    if (!selectedHourlyLocationId || !hourlyLocations.data) return;
    const loc = hourlyLocations.data.find((l) => l.id === selectedHourlyLocationId);
    if (loc) {
      setAddress(loc.addressLine1);
      setCity(loc.city);
      setState(loc.state);
    }
  }, [selectedHourlyLocationId, hourlyLocations.data]);

  // Pre-fill title from selected Hourly role
  useEffect(() => {
    if (!selectedHourlyRoleId || !hourlyRoles.data) return;
    const role = hourlyRoles.data.find((r) => r.id === selectedHourlyRoleId);
    if (role) {
      setTitle(role.name);
    }
  }, [selectedHourlyRoleId, hourlyRoles.data]);

  // Listen for company data updates
  useEffect(() => {
    const handleCompanyUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setCompanyData(customEvent.detail);
      }
    };
    
    window.addEventListener('passcom-company-updated', handleCompanyUpdate);
    return () => {
      window.removeEventListener('passcom-company-updated', handleCompanyUpdate);
    };
  }, []);
  
  // Load question templates on mount
  useEffect(() => {
    setApplicationTemplates(QuestionBankService.getTemplates('application'));
    setInterviewTemplates(QuestionBankService.getTemplates('interview'));
  }, []);
  
  // Check section completion
  const isDetailsComplete = () => {
    // Consider details complete when posting has a title
    return (title.trim() !== "");
  };
  
  const isCompensationComplete = () => {
    if (payOption === "omit") return true;
    if (payOption === "exact") return payExactAmount.trim() !== "";
    if (payOption === "range") return payRangeMin.trim() !== "" && payRangeMax.trim() !== "";
    return false;
  };
  
  
  const isQuestionsComplete = () => {
    return questions.length > 0;
  };
  
  // Auto-save function (extracted for reuse)
  const saveJob = useCallback(() => {
    const state = formStateRef.current;

    const payload: CreateJobPostingRequest = {
      title: state.title,
      companyName: companyData.companyName || "Company",
      locationName: [state.city, state.state].filter(Boolean).join(", ") || "Unknown",
      description: state.jobDescription,
      compensation: {
        min: parseFloat(state.payRangeMin) || undefined,
        max: parseFloat(state.payRangeMax) || undefined,
        type: state.payType.toLowerCase() === 'hourly' ? 'hourly' : 'salary',
      },
      scheduleConfiguration: {
        daysSlider: { defaultValue: 5, max: 7 },
        hoursSlider: { defaultValue: 8, max: 12 },
      },
      questions: state.questions.map(q => ({
        type: (q.type === 'Text' ? 'text' : q.type === 'Video' ? 'video' : q.type === 'Multiple Choice' ? 'multiple_choice' : 'text') as 'text' | 'multiple_choice' | 'yes_no',
        prompt: q.text,
        options: q.type === 'Multiple Choice' ? q.choices : undefined,
      })),
    };

    if (backendJobId) {
      // Update existing posting
      updateJobPosting.mutate({ id: backendJobId, data: payload });
    } else {
      // Create new posting and track the backend-assigned ID
      createJobPosting.mutate(payload, {
        onSuccess: (created) => {
          setBackendJobId(created.jobId);
        },
      });
    }
  }, [companyData, backendJobId, createJobPosting, updateJobPosting]);
  
  // Auto-save effect - debounced save on form changes
  useEffect(() => {
    // Mark as unsaved when form changes
    setSaveStatus('unsaved');
    
    // Debounce the save operation
    const timeoutId = setTimeout(() => {
      setSaveStatus('saving');
      saveJob();
      
      // Show "saved" status briefly
      setTimeout(() => {
        setSaveStatus('saved');
      }, 500);
    }, 1000); // Wait 1 second after last change before saving
    
    return () => clearTimeout(timeoutId);
  }, [
    jobId, title, jobCategory, jobDescription, skills, jobType, locationType,
    address, address2, city, state, zip, payType, payOption, payExactAmount,
    payRangeMin, payRangeMax, benefits, tipEligible, nonDiscriminationStatement,
    questions, interviewQuestions, saveJob
  ]);
  
  // Handle save/load events for postings (set up once, read from ref)
  useEffect(() => {
    const onSave = () => {
      saveJob();
    };
    const onLoad = (e: Event) => {
      const ce = e as CustomEvent<any>;
      const data = ce.detail;
      if (!data) return;
      setJobId(data.jobId || generateJobId());
      setTitle(data.title || "");
      setJobCategory(data.jobCategory || "");
      setJobDescription(data.jobDescription || "");
      setSkills(Array.isArray(data.skills) ? data.skills : []);
      setJobType(data.jobType || "Full-time");
      setLocationType(data.locationType || "On-site");
      setAddress(data.address || "");
      setAddress2(data.address2 || "");
      setCity(data.city || "");
      setState(data.state || "");
      setZip(data.zip || "");
      setPayType(data.payType || "Hourly");
      setPayOption(data.payOption ?? "exact");
      setPayExactAmount(data.payExactAmount ?? "");
      setPayRangeMin(data.payRangeMin ?? "");
      setPayRangeMax(data.payRangeMax ?? "");
      setBenefits(Array.isArray(data.benefits) ? data.benefits : []);
      setTipEligible(!!data.tipEligible);
      setNonDiscriminationStatement(data.nonDiscriminationStatement || "We are an Equal Opportunity Employer. All qualified applicants will receive consideration for employment without regard to race, color, religion, sex, sexual orientation, gender identity, national origin, age, disability, or veteran status.");
      setQuestions(Array.isArray(data.questions) && data.questions.length ? data.questions : [{ id: 1, type: "Text", text: "", limit: "500", choices: [], newChoice: "" }]);
      // Handle both old (string array) and new (object array) formats
      if (Array.isArray(data.interviewQuestions) && data.interviewQuestions.length) {
        if (typeof data.interviewQuestions[0] === 'string') {
          // Convert old format to new format
          setInterviewQuestions(data.interviewQuestions.map((q: string, i: number) => ({ id: `iq-${i}`, text: q })));
        } else {
          setInterviewQuestions(data.interviewQuestions);
        }
      } else {
        setInterviewQuestions([{ id: 'iq-1', text: '' }, { id: 'iq-2', text: '' }, { id: 'iq-3', text: '' }]);
      }
    };
    window.addEventListener('passcom-save-posting', onSave as EventListener);
    window.addEventListener('passcom-load-posting', onLoad as EventListener);
    return () => {
      window.removeEventListener('passcom-save-posting', onSave as EventListener);
      window.removeEventListener('passcom-load-posting', onLoad as EventListener);
    };
  }, []); // Empty array - only set up listeners once!
  
  // Auto-complete job when all sections are complete (debounced check)
  useEffect(() => {
    // Only check completion status occasionally to avoid performance issues
    const timeoutId = setTimeout(() => {
      if (onComplete && isDetailsComplete() && isCompensationComplete() && isQuestionsComplete()) {
        onComplete();
      }
    }, 500); // Check 500ms after last change
    
    return () => clearTimeout(timeoutId);
  }, [title, payOption, payExactAmount, payRangeMin, payRangeMax, questions, onComplete]); // Only essential fields

  const addQuestion = () => {
    const newId = questions.length > 0 ? Math.max(...questions.map(q => q.id)) + 1 : 1;
    setQuestions([...questions, { id: newId, type: "Text", text: "", limit: "500", choices: [], newChoice: "" }]);
  };

  const removeQuestion = (id: number) => {
    setQuestions(questions.filter(q => q.id !== id));
  };

  const updateQuestion = (id: number, updates: Partial<Question>) => {
    // Check if trying to change to Video type
    if (updates.type === "Video") {
      const existingVideoQuestion = questions.find(q => q.id !== id && q.type === "Video");
      if (existingVideoQuestion) {
        alert("Only one video question is allowed. Please change the existing video question to a different type first.");
        return;
      }
    }
    setQuestions(questions.map(q => q.id === id ? { ...q, ...updates } : q));
  };

  const addChoice = (id: number) => {
    const q = questions.find(q => q.id === id);
    if (q && q.newChoice.trim()) {
      updateQuestion(id, {
        choices: [...q.choices, q.newChoice.trim()],
        newChoice: ""
      });
    }
  };

  const removeChoice = (questionId: number, choiceIndex: number) => {
    const q = questions.find(q => q.id === questionId);
    if (q) {
      updateQuestion(questionId, {
        choices: q.choices.filter((_, i) => i !== choiceIndex)
      });
    }
  };

  // Question Bank handlers
  const handleSaveApplicationQuestions = () => {
    const name = prompt('Enter a name for this question template:');
    if (name && name.trim()) {
      QuestionBankService.saveTemplate('application', name.trim(), questions);
      setApplicationTemplates(QuestionBankService.getTemplates('application'));
      alert(`Template "${name}" saved successfully!`);
    }
  };

  const handleLoadApplicationQuestions = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = e.target.value;
    if (!templateId) return;
    
    const template = QuestionBankService.getTemplate(templateId);
    if (template) {
      setQuestions(template.questions);
    }
    // Reset select
    e.target.value = '';
  };

  const handleSaveInterviewQuestions = () => {
    const name = prompt('Enter a name for this interview script template:');
    if (name && name.trim()) {
      QuestionBankService.saveTemplate('interview', name.trim(), interviewQuestions);
      setInterviewTemplates(QuestionBankService.getTemplates('interview'));
      alert(`Template "${name}" saved successfully!`);
    }
  };

  const handleLoadInterviewQuestions = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = e.target.value;
    if (!templateId) return;
    
    const template = QuestionBankService.getTemplate(templateId);
    if (template) {
      setInterviewQuestions(template.questions);
    }
    // Reset select
    e.target.value = '';
  };


  const sections = [
    { id: 'details' as SectionTab, label: 'Job Details', isComplete: isDetailsComplete },
    { id: 'compensation' as SectionTab, label: 'Pay', isComplete: isCompensationComplete },
    { id: 'questions' as SectionTab, label: 'Questions', isComplete: isQuestionsComplete },
    { id: 'preview' as SectionTab, label: 'Preview / QR', isComplete: () => true }
  ];

  return (
    <div id="tour-posting" className="space-y-4">
      {/* Section Tabs */}
      <div className="border-b">
        <div className="flex gap-1 items-center justify-between">
        <div className="flex gap-1">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 font-medium text-sm transition-all border-b-2 flex items-center gap-2 ${
                activeSection === section.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <span>{section.label}</span>
              {section.isComplete() && <span className="text-green-600">✓</span>}
            </button>
          ))}
          </div>
          
          {/* Auto-save Status Indicator */}
          <div className="flex items-center gap-2 px-3 py-1.5 text-sm">
            {saveStatus === 'saving' && (
              <>
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                <span className="text-gray-500">Saving...</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Section Content */}
      <div className="space-y-4">
        {activeSection === 'details' && (
          <div className="bg-white border rounded-xl p-5 shadow-sm">
            <div className="grid grid-cols-12 gap-3">
              <Field label="Job ID">
                <Input 
                  placeholder="e.g., ABC123XY" 
                  value={jobId}
                  onChange={(e) => setJobId(e.target.value)}
                  className="font-mono"
                  maxLength={8}
                />
              </Field>

              <Field label="Title">
                <Input 
                  placeholder="Line Cook" 
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>

              <Field label="Job Category">
                <select
                  className="w-full h-9 px-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={jobCategory}
                  onChange={(e) => setJobCategory(e.target.value)}
                >
                  <option value="">Select category...</option>
                  {JOB_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </Field>

              {/* Hourly Role — wired to Hourly's job-roles API (PCO-71) */}
              <Field label="Hourly Role">
                <select
                  className="w-full h-9 px-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedHourlyRoleId}
                  onChange={(e) => setSelectedHourlyRoleId(e.target.value)}
                  disabled={!hasHourlyToken || hourlyRoles.isLoading || hourlyRoles.isError}
                >
                  {!hasHourlyToken ? (
                    <option value="">Log in to Hourly to load roles</option>
                  ) : hourlyRoles.isLoading ? (
                    <option value="">Loading roles…</option>
                  ) : hourlyRoles.isError ? (
                    <option value="">Failed to load roles</option>
                  ) : (
                    <>
                      <option value="">Select Hourly role…</option>
                      {(hourlyRoles.data ?? []).map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                          {role.jobCode ? ` (${role.jobCode})` : ''}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </Field>

              {/* Hourly Location — wired to Hourly's locations API (PCO-71) */}
              <Field label="Hourly Location">
                <select
                  className="w-full h-9 px-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedHourlyLocationId}
                  onChange={(e) => setSelectedHourlyLocationId(e.target.value)}
                  disabled={!hasHourlyToken || hourlyLocations.isLoading || hourlyLocations.isError}
                >
                  {!hasHourlyToken ? (
                    <option value="">Log in to Hourly to load locations</option>
                  ) : hourlyLocations.isLoading ? (
                    <option value="">Loading locations…</option>
                  ) : hourlyLocations.isError ? (
                    <option value="">Failed to load locations</option>
                  ) : (
                    <>
                      <option value="">Select Hourly location…</option>
                      {(hourlyLocations.data ?? []).map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name
                            ? `${loc.name} — ${loc.city}, ${loc.state}`
                            : `${loc.city}, ${loc.state}`}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </Field>

              <Field label="Job Type">
                <select 
                  className="w-full h-9 px-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={jobType}
                  onChange={(e) => setJobType(e.target.value)}
                >
                  <option>Full-time</option>
                  <option>Part-time</option>
                  <option>Contract</option>
                  <option>Temporary</option>
                  <option>Internship</option>
                </select>
              </Field>

              <Field label="Location Type">
                <select 
                  className="w-full h-9 px-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={locationType}
                  onChange={(e) => setLocationType(e.target.value)}
                >
                  <option>On-site</option>
                  <option>Hybrid</option>
                  <option>Remote</option>
                </select>
              </Field>

              <Field span="col-span-12 lg:col-span-8" label="Address">
                <Input 
                  placeholder="e.g., 123 Main Street" 
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </Field>

              <Field span="col-span-12 lg:col-span-4" label="Address 2">
                <Input 
                  placeholder="Suite, unit, etc. (optional)" 
                  value={address2}
                  onChange={(e) => setAddress2(e.target.value)}
                />
              </Field>

              <Field span="col-span-12 lg:col-span-6" label="City">
                <Input 
                  placeholder="e.g., Boston" 
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </Field>

              <Field span="col-span-12 lg:col-span-3" label="State">
                <Input 
                  placeholder="e.g., MA" 
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
              </Field>

              <Field span="col-span-12 lg:col-span-3" label="Zip Code">
                <Input 
                  placeholder="e.g., 02128" 
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                />
              </Field>

            <Field span="col-span-12" label="Job Description">
              <Textarea 
                placeholder="Describe duties and environment…" 
                rows={4}
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
              />
            </Field>

            <Field span="col-span-12 lg:col-span-6" label="Required Skills">
              <div className="flex flex-wrap gap-2 mb-2">
                {skills.map((s, i) => <Chip key={i} onRemove={() => setSkills(prev => prev.filter((_, j) => j !== i))}>{s}</Chip>)}
              </div>
              <Input placeholder="e.g., ServSafe" value={newSkill}
                onChange={e => setNewSkill(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && newSkill.trim()) { setSkills(p => [...p, newSkill.trim()]); setNewSkill(""); e.preventDefault(); } }}
              />
            </Field>
            </div>
          </div>
        )}

        {activeSection === 'compensation' && (
          <div className="bg-white border rounded-xl p-5 shadow-sm">
            <div className="grid grid-cols-12 gap-3">
            <Field label="Pay Type">
              <select 
                className="w-full h-9 px-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={payType}
                onChange={(e) => setPayType(e.target.value)}
              >
                <option>Hourly</option>
                <option>Salary</option>
              </select>
            </Field>
            
            <Field span="col-span-12">
              <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <label className="flex items-center gap-2 text-[13px] text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="payOption"
                    checked={payOption === "exact"}
                    onChange={() => setPayOption("exact")}
                    className="w-4 h-4"
                  />
                  Exact amount
                </label>
                <Input 
                  placeholder="e.g., $18.50" 
                  value={payExactAmount}
                  onChange={(e) => setPayExactAmount(e.target.value)}
                  disabled={payOption !== "exact"} 
                  className={`w-[150px] ${payOption !== "exact" ? "opacity-50" : ""}`} 
                />

                <label className="flex items-center gap-2 text-[13px] text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="payOption"
                    checked={payOption === "range"}
                    onChange={() => setPayOption("range")}
                    className="w-4 h-4"
                  />
                  Pay range
                </label>
                <div className="flex gap-2 items-center">
                  <Input 
                    placeholder="Min" 
                    value={payRangeMin}
                    onChange={(e) => setPayRangeMin(e.target.value)}
                    disabled={payOption !== "range"} 
                    className={`w-[150px] ${payOption !== "range" ? "opacity-50" : ""}`} 
                  />
                  <span className="text-gray-400">–</span>
                  <Input 
                    placeholder="Max" 
                    value={payRangeMax}
                    onChange={(e) => setPayRangeMax(e.target.value)}
                    disabled={payOption !== "range"} 
                    className={`w-[150px] ${payOption !== "range" ? "opacity-50" : ""}`} 
                  />
                </div>

                <label className="flex items-center gap-2 text-[13px] text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="payOption"
                    checked={payOption === "omit"}
                    onChange={() => setPayOption("omit")}
                    className="w-4 h-4"
                  />
                  Omit pay
                </label>
              </div>
              <div className={`mt-2 text-sm ${payOption === "omit" ? "text-gray-600" : "text-gray-400"}`}>
                {payOption === "omit" ? "Compensation information will not be displayed" : ""}
              </div>
            </Field>

            <Field span="col-span-12 lg:col-span-6" label="Benefits">
              <div className="flex flex-wrap gap-2 mb-2">
                {benefits.map((b, i) => <Chip key={i} onRemove={() => setBenefits(prev => prev.filter((_, j) => j !== i))}>{b}</Chip>)}
              </div>
              <Input placeholder="e.g., 401(k)" value={newBenefit}
                onChange={e => setNewBenefit(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && newBenefit.trim()) { setBenefits(p => [...p, newBenefit.trim()]); setNewBenefit(""); e.preventDefault(); } }}
              />
            </Field>

            <Field span="col-span-12 lg:col-span-6" label="Tip Eligibility">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={tipEligible}
                  onChange={(e) => setTipEligible(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300" 
                />
                <span>Position is eligible for tips</span>
              </label>
            </Field>
            </div>
          </div>
        )}

        {activeSection === 'questions' && (
          <div className="space-y-6">
            {/* Application Questions Section */}
            <div className="bg-white border rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Application Questions</h3>
                
                {/* Load Template Dropdown */}
                <select
                  onChange={handleLoadApplicationQuestions}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  defaultValue=""
                >
                  <option value="">Load from Bank...</option>
                  {applicationTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-12 gap-3">
                
                <div className="col-span-12 grid grid-cols-12 gap-3">
                {questions.map((q, idx) => {
                  // Check if there's already a video question (and it's not this one)
                  const hasVideoQuestion = questions.some(question => question.id !== q.id && question.type === "Video");
                  
                  return (
                  <div key={q.id} className="col-span-12 lg:col-span-6 border rounded p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-600">Question {idx + 1}</span>
                      <button onClick={() => removeQuestion(q.id)} className="text-red-600 hover:underline text-xs">Remove</button>
                    </div>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <select
                          className="flex-1 h-8 px-2 rounded border border-gray-300 text-sm"
                          value={q.type}
                          onChange={(e) => updateQuestion(q.id, {
                            type: e.target.value as QuestionType,
                            limit: e.target.value === "Video" ? "60" : "500",
                            choices: e.target.value === "Multiple Choice" ? (q.choices.length > 0 ? q.choices : ["Option 1"]) : []
                          })}
                        >
                          <option>Text</option>
                          <option disabled={hasVideoQuestion} title={hasVideoQuestion ? "Only one video question is allowed" : ""}>
                            Video{hasVideoQuestion ? " (limit reached)" : ""}
                          </option>
                          <option>Multiple Choice</option>
                        </select>
                        {(q.type === "Text" || q.type === "Video") && (
                          <input
                            type="number"
                            placeholder="Limit"
                            value={q.limit}
                            onChange={(e) => updateQuestion(q.id, { limit: e.target.value })}
                            className="w-20 h-8 px-2 rounded border border-gray-300 text-sm"
                          />
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="Question text"
                        value={q.text}
                        onChange={(e) => updateQuestion(q.id, { text: e.target.value })}
                        className="w-full h-8 px-2 rounded border border-gray-300 text-sm"
                      />
                      {q.type === "Multiple Choice" && (
                        <div className="space-y-1">
                          <div className="text-xs text-gray-500">Answer Options:</div>
                          {q.choices.map((choice, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={choice}
                                onChange={(e) => {
                                  const newChoices = [...q.choices];
                                  newChoices[i] = e.target.value;
                                  updateQuestion(q.id, { choices: newChoices });
                                }}
                                className="flex-1 text-sm px-2 py-1 bg-white rounded border border-gray-300"
                              />
                              <button onClick={() => removeChoice(q.id, i)} className="text-red-600 text-xs hover:text-red-800">×</button>
                            </div>
                          ))}
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="New option"
                              value={q.newChoice}
                              onChange={(e) => updateQuestion(q.id, { newChoice: e.target.value })}
                              onKeyDown={(e) => { if (e.key === 'Enter') { addChoice(q.id); e.preventDefault(); } }}
                              className="flex-1 h-8 px-2 rounded border border-gray-300 text-sm"
                            />
                            <button onClick={() => addChoice(q.id)} className="px-2 h-8 bg-blue-600 text-white rounded text-xs">Add</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
                })}
                </div>
                <div className="col-span-12 flex items-center justify-between">
                  <button onClick={addQuestion} className="text-sm text-blue-600 hover:underline">+ Add Question</button>
                  <button 
                    onClick={handleSaveApplicationQuestions}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Save Questions
                  </button>
                </div>
              </div>
            </div>

            {/* Interview Questions Section */}
            <div className="bg-white border rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Interview Questions</h3>
                
                {/* Load Template Dropdown */}
                <select
                  onChange={handleLoadInterviewQuestions}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  defaultValue=""
                >
                  <option value="">Load from Bank...</option>
                  {interviewTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <InterviewQuestionsSection
                questions={interviewQuestions}
                onChange={setInterviewQuestions}
              />
              
              <div className="flex justify-end mt-3">
                <button 
                  onClick={handleSaveInterviewQuestions}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Save Questions
                </button>
              </div>
            </div>

            {/* Non-Discrimination Statement Section */}
            <div className="bg-white border rounded-xl p-5 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Non-Discrimination Statement</h3>
              <Textarea 
                placeholder="Enter non-discrimination statement..." 
                rows={3}
                value={nonDiscriminationStatement}
                onChange={(e) => setNonDiscriminationStatement(e.target.value)}
              />
            </div>
          </div>
        )}

        {activeSection === 'preview' && (
          <div className="bg-white border rounded-xl p-8 shadow-sm max-w-4xl mx-auto">
            <div className="space-y-6">
              {/* Company Information */}
              <div className="flex items-start gap-4 mb-4">
                {companyData.companyLogo && (
                  <img 
                    src={companyData.companyLogo} 
                    alt={companyData.companyName}
                    className="w-16 h-16 object-contain"
                  />
                )}
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900">{companyData.companyName || "Company Name"}</h2>
                  {companyData.companyWebsite && (
                    <a 
                      href={companyData.companyWebsite} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {companyData.companyWebsite}
                    </a>
                  )}
                  {companyData.companyAddress && (
                    <p className="text-sm text-gray-600 mt-1">{companyData.companyAddress}</p>
                  )}
                  {companyData.contactEmail && (
                    <p className="text-sm text-gray-600">
                      <a href={`mailto:${companyData.contactEmail}`} className="text-blue-600 hover:underline">
                        {companyData.contactEmail}
                      </a>
                    </p>
                  )}
                </div>
              </div>

              {/* Job Header with QR Code */}
              <div className="flex items-start justify-between gap-6 mb-4">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-3">{title || "Job Title"}</h1>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                    {jobCategory && <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full">{jobCategory}</span>}
                    {jobType && <span className="bg-gray-100 px-3 py-1 rounded-full">{jobType}</span>}
                    {locationType && <span className="bg-gray-100 px-3 py-1 rounded-full">{locationType}</span>}
                  </div>
                </div>
                
                {/* QR Code Box */}
                <div className="flex-shrink-0 bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600 mb-2">Find this Job at:</p>
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`https://jobs.passcom.com/apply/${title.toLowerCase().replace(/\s+/g, '-') || 'job'}`)}`}
                    alt="QR Code"
                    className="w-24 h-24 mx-auto mb-2"
                  />
                  <a 
                    href={`https://jobs.passcom.com/apply/${title.toLowerCase().replace(/\s+/g, '-') || 'job'}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline break-all"
                  >
                    jobs.passcom.com/apply/{title.toLowerCase().replace(/\s+/g, '-') || 'job'}
                  </a>
                </div>
              </div>

              {/* Horizontal line below QR code box */}
              <div className="border-b pb-6"></div>

              {/* Location */}
              {(address || city || state) && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Location</h2>
                  <p className="text-gray-700">
                    {address && <span>{address}</span>}
                    {address2 && <span>, {address2}</span>}
                    {(address || address2) && (city || state || zip) && <br />}
                    {city && <span>{city}</span>}
                    {city && state && <span>, </span>}
                    {state && <span>{state}</span>}
                    {zip && <span> {zip}</span>}
                  </p>
                </div>
              )}

              {/* Job Description */}
              {jobDescription && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Job Description</h2>
                  <p className="text-gray-700 whitespace-pre-line">{jobDescription}</p>
                </div>
              )}

              {/* Required Skills */}
              {skills.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Required Skills</h2>
                  <div className="flex flex-wrap gap-2">
                    {skills.map((skill, i) => (
                      <span key={i} className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Compensation */}
              {payOption !== 'omit' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Compensation</h2>
                  <div className="text-gray-700 space-y-1">
                    <p>
                      <span className="font-medium">{payType}:</span>{' '}
                      {payOption === 'exact' && payExactAmount && `$${payExactAmount}`}
                      {payOption === 'range' && payRangeMin && payRangeMax && `$${payRangeMin} - $${payRangeMax}`}
                    </p>
                    {tipEligible && <p className="text-sm text-green-600">✓ Tips eligible</p>}
                  </div>
                </div>
              )}

              {/* Benefits */}
              {benefits.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Benefits</h2>
                  <div className="flex flex-wrap gap-2">
                    {benefits.map((benefit, i) => (
                      <span key={i} className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm">
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Schedule */}

              {/* Application Questions */}
              {questions.length > 0 && questions.some(q => q.text) && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">Application Questions</h2>
                  <div className="space-y-3">
                    {questions.filter(q => q.text).map((q, idx) => (
                      <div key={q.id} className="bg-gray-50 p-3 rounded border">
                        <p className="font-medium text-gray-900 mb-1">{idx + 1}. {q.text}</p>
                        <p className="text-xs text-gray-500">
                          {q.type === "Text" && `Text response (max ${q.limit} characters)`}
                          {q.type === "Video" && `Video response (max ${q.limit} seconds)`}
                          {q.type === "Multiple Choice" && `Multiple choice`}
                        </p>
                        {q.type === "Multiple Choice" && q.choices.length > 0 && (
                          <ul className="mt-2 ml-4 text-sm text-gray-600 list-disc">
                            {q.choices.map((choice, i) => (
                              <li key={i}>{choice}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Non-Discrimination Statement */}
              {nonDiscriminationStatement && (
                <div className="border-t pt-6">
                  <p className="text-sm text-gray-600 italic">{nonDiscriminationStatement}</p>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

